import { Injectable } from '@angular/core';
import { SignalRService } from './signalR.service';
import { StateType, SubscriptionType } from '../models';
import { EntityType, InspectionSession, InspectionSessionsModel } from '../models/multi-edit.model';
import { DataService } from './data.service';
import { fromEvent, merge, Subscription } from 'rxjs';
import { BaseService } from './baseService';
import { debounceTime, distinctUntilChanged, filter, map, startWith, take, takeWhile } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SessionDetectService extends BaseService {
  private readonly PALETTE = ['#1d4ed8', '#15803d', '#b45309', '#b91c1c', '#6d28d9', '#0e7490', '#9f1239', '#065f46', '#c2410c', '#374151'];
  private colorIndex = 0;
  private colorByKey = new Map<string, string>();

  private sessionsSub?: Subscription;
  private routerSub?: Subscription;
  private awaySub?: Subscription;
  private exitSub?: Subscription;
  private inspectionId!: string;
  private lastState: any;

  constructor(private router: Router, private signalR: SignalRService, private dataService: DataService) {
    super();
  }

  activateUserDetection(inspectionId: string) {
    const key = `${inspectionId}-users`;

    if (this.inspectionId === inspectionId) return;

    this.wireExitClear(key);
    this.inspectionId = inspectionId;

    let take = true;

    this.routerSub?.unsubscribe();
    this.routerSub = this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        map(() => this.pickEntity(this.router.url)),
        debounceTime(200),
        distinctUntilChanged((a, b) => a.type === b.type && a.id === b.id),
        takeWhile((res) => take, false)
      )
      .subscribe((res) => {
        take = !!res.type;
        this.lastState =
          res.type && this.currentUserName
            ? {
                entityType: res.type,
                entityId: res.id,
                user: this.currentUserName,
                date: new Date(),
                url: this.router.url
              }
            : null;
        this.signalR.patchState(
          {
            key,
            compressionKey: this.sessionId,
            sessionId: this.sessionId,
            data: {
              [this.sessionId]: this.lastState
            }
          },
          StateType.Platform
        );
      });

    this.sessionsSub?.unsubscribe();
    if (this.inspectionId) this.signalR.unsubscribe(key, StateType.Platform);

    this.sessionsSub = this.signalR
      .subscribeFor(key, StateType.Platform, { subscriptionType: SubscriptionType.Latest })
      .subscribe((res) => {
        if (!res.length || !res[0].valueData) return;
        const sessions: InspectionSessionsModel = JSON.parse(res[0].valueData);
        const date = new Date();
        date.setMinutes(date.getMinutes() - 10);
        const validSessions = [];
        for (const sessionId in sessions) {
          const session = Object.assign(new InspectionSession(), sessions[sessionId]);
          session.date = new Date(session.date);
          if (sessionId === this.sessionId || session.date < date || !session.user) continue;
          session.color = this.getColor(sessionId);
          validSessions.push(session);
        }

        const byTime = (a: InspectionSession, b: InspectionSession) => a.date.getTime() - b.date.getTime();
        this.dataService.processSessions(inspectionId, validSessions.sort(byTime));
      });
  }

  deactivate() {
    if (!this.inspectionId) return;
    const key = `${this.inspectionId}-users`;
    this.sessionsSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.signalR.unsubscribe(key, StateType.Platform);
  }

  navigate(session: InspectionSession) {
    if (!session.entityType || !session.entityId) return;
    const url = session.url.replace(/^\/[^/]+/, '');
    this.router.navigateByUrl(`/${environment.applicationRoute}${url}`);
  }

  private wireExitClear(key: string) {
    this.awaySub?.unsubscribe();
    this.exitSub?.unsubscribe();
    this.exitSub = merge(fromEvent(window, 'pagehide'), fromEvent(window, 'beforeunload'))
      .pipe(take(1))
      .subscribe(() => {
        const data = {
          key,
          compressionKey: this.sessionId,
          sessionId: this.sessionId,
          data: { [this.sessionId]: null }
        };
        this.signalR.patchState(data, StateType.Platform);
      });

    this.awaySub = fromEvent(document, 'visibilitychange')
      .pipe(debounceTime(200))
      .subscribe(() => {
        if (!this.lastState || !this.currentUserName) return;

        if (document.visibilityState === 'hidden') {
          this.lastState.hidden = true;
          this.signalR.patchState(
            {
              key,
              compressionKey: this.sessionId,
              sessionId: this.sessionId,
              data: {
                [this.sessionId]: this.lastState
              }
            },
            StateType.Platform
          );
          return;
        }

        this.lastState.hidden = false;
        this.lastState.date = new Date();
        this.signalR.patchState(
          {
            key,
            compressionKey: this.sessionId,
            sessionId: this.sessionId,
            data: {
              [this.sessionId]: this.lastState
            }
          },
          StateType.Platform
        );
      });
  }

  private getColor(key: string): string {
    const existing = this.colorByKey.get(key);
    if (existing) return existing;
    const color = this.PALETTE[this.colorIndex];
    this.colorIndex = (this.colorIndex + 1) % this.PALETTE.length;
    this.colorByKey.set(key, color);
    return color;
  }

  private readonly guid = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

  private pickEntity(url: string): { type: EntityType | null; id: string | null } {
    const clean = url.split('?')[0].split('#')[0];

    const first = (label: string, type: EntityType) => {
      const re = new RegExp(`/${label}/(${this.guid.source})`, 'i');
      const m = clean.match(re);
      return m ? { type, id: m[1] } : null;
    };

    return (
      first('plugins', EntityType.Plugin) ??
      first('points', EntityType.Point) ??
      first('folders', EntityType.Folder) ??
      first('sections', EntityType.Section) ??
      first('inspections', EntityType.Inspection) ?? { type: null, id: null }
    );
  }
}
